package com.evoapps.evoshift.viewmodels

import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import com.evoapps.evoshift.domain.model.DayRange
import com.evoapps.evoshift.domain.model.RosterIds
import com.evoapps.evoshift.domain.model.RosterMailDelivery
import com.evoapps.evoshift.domain.model.RosterMailResult
import com.evoapps.evoshift.domain.usecase.auth.GetCurrentUserUseCase
import com.evoapps.evoshift.domain.usecase.billing.ObservePlanActiveUseCase
import com.evoapps.evoshift.domain.usecase.calendar.EmailRosterUseCase
import com.evoapps.evoshift.observability.AnalyticsEvent
import com.evoapps.evoshift.observability.AppTelemetry
import com.evoapps.evoshift.ui.util.UiText
import com.evoapps.evoshift.ui.util.toUiText
import evoshift.composeapp.generated.resources.Res
import evoshift.composeapp.generated.resources.email_roster_failed
import evoshift.composeapp.generated.resources.email_roster_nobody
import evoshift.composeapp.generated.resources.email_roster_queued
import evoshift.composeapp.generated.resources.email_roster_sent
import evoshift.composeapp.generated.resources.email_roster_still_sending
import evoshift.composeapp.generated.resources.email_roster_without_email
import evoshift.composeapp.generated.resources.email_roster_without_email_hint
import evoshift.composeapp.generated.resources.print_not_allowed
import kotlinx.coroutines.Job
import kotlinx.coroutines.delay
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.first
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch

/**
 * "Email the roster": [sent] lists what happened once it went out; SH-242: while
 * [checkingDelivery] the app asks the server, a few times over about half a minute, whether the
 * mail service took the e-mails, and [sent] then says how many went, to whom they could not be
 * delivered and how many are still on their way.
 */
data class RosterMailState(
    val sending: Boolean = false,
    val error: UiText? = null,
    val sent: List<UiText> = emptyList(),
    val checkingDelivery: Boolean = false
)

/**
 * "Email the roster" (SH-45, roster builder): the owner or a manager sends a published month to the
 * team or one person, for the month or some days. The server (sendRosterEmail) checks the same
 * permission, that the month is published, the plan and a 10-minute limit per recipient.
 */
class RosterMailViewModel(
    private val emailRoster: EmailRosterUseCase,
    private val getCurrentUser: GetCurrentUserUseCase,
    private val observePlanActive: ObservePlanActiveUseCase? = null
) : ViewModel() {
    private val _state = MutableStateFlow(RosterMailState())
    val state = _state.asStateFlow()
    private var deliveryCheck: Job? = null

    companion object {
        /** SH-242: when the delivery is checked, after sending (milliseconds between checks). */
        val DELIVERY_CHECKS_MS = listOf(3_000L, 5_000L, 7_000L, 10_000L, 15_000L)
    }

    /** A fresh dialog: no earlier result or error. */
    fun reset() {
        if (_state.value.sending) return
        deliveryCheck?.cancel()
        _state.value = RosterMailState()
    }

    fun send(rosterId: String, userId: String?, days: DayRange?) {
        if (_state.value.sending) return
        deliveryCheck?.cancel()
        _state.update { it.copy(sending = true, error = null, sent = emptyList(), checkingDelivery = false) }
        viewModelScope.launch {
            val workplaceId = RosterIds.workplaceIdOf(rosterId)
            val allowed = capabilitiesAt(getCurrentUser(), observePlanActive, workplaceId).first().shows(Gate.EMAIL_ROSTER)
            if (!allowed) {
                _state.update { it.copy(sending = false, error = UiText.Resource(Res.string.print_not_allowed)) }
                return@launch
            }
            emailRoster(rosterId, userId, days).fold(
                onSuccess = { result ->
                    AppTelemetry.log(AnalyticsEvent.RosterEmailed(everyone = userId == null, wholeMonth = days == null))
                    val checking = result.mailIds.isNotEmpty()
                    _state.update { it.copy(sending = false, sent = queuedLines(result), checkingDelivery = checking) }
                    if (checking) deliveryCheck = viewModelScope.launch { checkDelivery(result) }
                },
                onFailure = { e -> _state.update { it.copy(sending = false, error = e.toUiText()) } }
            )
        }
    }

    /**
     * Right after sending: nobody to send to; or the e-mails on their way (before SH-242's server:
     * "sent"); and who has no address.
     */
    private fun queuedLines(result: RosterMailResult): List<UiText> = buildList {
        when {
            result.queued == 0 -> add(UiText.Resource(Res.string.email_roster_nobody))
            result.mailIds.isEmpty() -> add(UiText.Resource(Res.string.email_roster_sent, result.queued))
            else -> add(UiText.Resource(Res.string.email_roster_queued, result.queued))
        }
        addAll(withoutEmailLines(result))
    }

    private fun withoutEmailLines(result: RosterMailResult): List<UiText> =
        if (result.withoutEmail <= 0) emptyList() else listOf(
            UiText.Resource(Res.string.email_roster_without_email, names(result.withoutEmailNames, result.withoutEmail)),
            UiText.Resource(Res.string.email_roster_without_email_hint)
        )

    /** The people's names, or how many when the server sent none. */
    private fun names(names: List<String>, count: Int): String =
        names.filter { it.isNotBlank() }.joinToString(", ").ifEmpty { count.toString() }

    /** Asks until nothing is on its way any more (or the last check); a failed check keeps what is shown. */
    private suspend fun checkDelivery(result: RosterMailResult) {
        var last: RosterMailDelivery? = null
        for (wait in DELIVERY_CHECKS_MS) {
            delay(wait)
            val delivery = emailRoster.delivery(result.mailIds).getOrNull() ?: break
            last = delivery
            if (delivery.pending == 0) break
        }
        val delivery = last
        _state.update { state ->
            if (delivery == null) return@update state.copy(checkingDelivery = false)
            state.copy(
                checkingDelivery = false,
                sent = buildList {
                    add(UiText.Resource(Res.string.email_roster_sent, delivery.sent))
                    if (delivery.failed > 0) add(UiText.Resource(Res.string.email_roster_failed, names(delivery.failedNames, delivery.failed)))
                    if (delivery.pending > 0) add(UiText.Resource(Res.string.email_roster_still_sending, delivery.pending))
                    addAll(withoutEmailLines(result))
                }
            )
        }
    }
}
